/**
 * Walk it through on a phone and an iPad: one stop per screen with three dots, the guest's own look as the preview, a
 * field or two as sheets, and one docked action ("Looks right", then Publish). Upright puts the preview and the fields in
 * two columns; on its side the stops, the preview in a phone and the fields or the chat are three panes. It reads and
 * changes the same board as the computer's walk, through the same hooks.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { DockedBar } from "../../components/season/DockedBar";
import type { SeasonLayout } from "../../components/season/layout";
import { Page } from "../../components/season/Page";
import { Pane, Panes } from "../../components/season/Panes";
import { PushedNav } from "../../components/season/PushedNav";
import { SeasonCard } from "../../components/season/Card";
import { GroupedList, ListRow } from "../../components/season/GroupedList";
import { Switch } from "../../components/season/Switch";
import { Button } from "../../components/ui/Button";
import { Icon } from "../../components/ui/Icon";
import { useToast } from "../../components/ui/Toast";
import type { BoardState } from "../../../shared/types";
import type { BoardHost } from "../host";
import { FOLLOW_UP_LIBRARY, libraryTaken } from "../followups";
import { mmss } from "../make/time";
import { Sheet } from "../../components/ui/Sheet";
import { ActionsSheet } from "../../components/season/ActionsSheet";
import { followName, type Mail } from "../walk/followRows";
import { handoutPick, offerFilePick, roomEditing, videoNotePick } from "../walk/roomEdits";
import { KIND, momentsOf, PIN_END, PIN_START, roomDuration, useRoomFiles } from "../walk/roomParts";
import { RoomPreview } from "../walk/RoomPreview";
import { useNoteMedia } from "../walk/noteMedia";
import { STEP_NAMES, useWalk, WALK_STEPS, type WalkStep } from "../walk/useWalk";
import { asksIn, WHOLE_PAGE } from "../walk/pageStudio";
import { MomentFields, OfferFields } from "../walk/WalkFields";
import { Composer, Thread } from "./SeasonPageChat";
import { SeasonPageFrame, type TappedSlot } from "./SeasonPageFrame";
import {
  AddressSheet,
  FieldsSheet,
  FollowRows,
  FollowSheet,
  FollowSteps,
  MomentRows,
  OfferRows,
  OpenPage,
  PublishSheet,
  StartOverSheet,
  StopDots,
  StopList,
  TextSheet,
} from "./seasonWalkParts";
import "../../styles/season-make.css";

type Open =
  | { k: "text"; tapped: TappedSlot }
  | { k: "address" }
  | { k: "moment"; id: string }
  | { k: "offer"; focus: string | null }
  | { k: "follow"; id: string }
  | { k: "addFollow" }
  | { k: "menu" }
  | { k: "over" }
  | { k: "publish" }
  | { k: "page" };

const ROOMS = "/rooms";

export function SeasonWalk({ layout, state, host }: { layout: SeasonLayout; state: BoardState; host: BoardHost }) {
  const w = useWalk(state, host);
  const toast = useToast();
  const { step, at: stepAt, go, looked, published, live, server, studio, offer, base, roomName, address } = w;
  const phone = layout === "phone";
  const lib = useRoomFiles(state);
  const editing = roomEditing(host, w.remember);
  const [open, setOpen] = useState<Open | null>(null);
  const [part, setPart] = useState<string>(WHOLE_PAGE);
  const [peek, setPeek] = useState(false);
  const [shownId, setShownId] = useState<string | null>(null);
  const [offerShown, setOfferShown] = useState(false);
  // Why a ref: "Go to the offer" changes the stop, and a new stop starts with nothing open, so it asks for the offer's fields here.
  const offerNext = useRef(false);
  const [showPublished, setShowPublished] = useState(false);
  const [fsel, setFsel] = useState<string | null>(null);
  const chat = phone && step === "page" ? w.params.get("chat") : null;
  const close = () => setOpen(null);

  const moments = useMemo(() => momentsOf(state), [state]);
  const shown = moments.find((m) => m.id === shownId) ?? moments.find((m) => m.on) ?? moments[0];
  const dur = roomDuration(state, host);
  const second = offerShown && offer ? Math.min(dur - 1, (offer.t ?? 0) + 4) : shown ? (shown.t ?? 0) + 3 : 60;
  const notePoster = useNoteMedia(offer?.videoNote).poster;
  const mails = state.mail.filter((c): c is Mail => c.kind === "mail");
  const sessions = server.scheduleMode !== undefined && server.scheduleMode !== "on_demand";
  const followNow = mails.find((m) => m.id === fsel) ?? mails[0];

  // A different stop starts at its top with nothing open.
  useEffect(() => {
    setOpen(offerNext.current ? { k: "offer", focus: null } : null);
    setOfferShown(offerNext.current);
    offerNext.current = false;
    window.scrollTo({ top: 0 });
    document.querySelector(".main")?.scrollTo({ top: 0 });
  }, [step]);

  // The page is live: its screen opens, and a later look at the walk comes back to the stops.
  useEffect(() => {
    if (host.publishState !== "done") return;
    setOpen(null);
    setShowPublished(true);
  }, [host.publishState]);

  const label = (name: string) => (name === WHOLE_PAGE ? "The whole page" : (studio.sections.find((s) => s.name === name)?.label ?? "This part"));
  const chatSection = phone ? (chat ?? WHOLE_PAGE) : part;
  const openChat = (section: string) => w.setParams({ step: "page", chat: section });
  const backToPage = () => w.setParams({ step: "page" });

  // --- what a tap on the page does ---
  const onSlot = (tapped: TappedSlot) => setOpen({ k: "text", tapped });
  const onPart = (section: string) => {
    if (phone) openChat(section);
    else setPart(section);
  };
  const saveSlot = (tapped: TappedSlot, text: string) => {
    const was = tapped.text;
    close();
    if (text === was) return;
    if (tapped.section === "chapters" && tapped.slot === "chapter") {
      const card = w.chaptersCard;
      if (card && card.kind === "chapters" && card.rows[tapped.index]) {
        const rows = card.rows.map((r, k) => (k === tapped.index ? { ...r, title: text.slice(0, 80) } : r));
        host.dispatch({ type: "edit", id: card.id, patch: { rows } as never });
        const back = w.remember(() => host.dispatch({ type: "edit", id: card.id, patch: { rows: card.rows } as never }));
        toast({ text: "Saved.", icon: "check", action: { label: "Undo", onClick: back } });
      }
      return;
    }
    void studio.words(tapped.section, tapped.slot, tapped.index, text);
    const back = w.remember(() => void studio.words(tapped.section, tapped.slot, tapped.index, was));
    toast({ text: "Saved.", icon: "check", action: { label: "Undo", onClick: back } });
  };
  const takeOut = (tapped: TappedSlot) => {
    close();
    void studio.words(tapped.section, tapped.slot, tapped.index, "", "out");
    const back = w.remember(() => void studio.words(tapped.section, tapped.slot, tapped.index, tapped.text, "put"));
    toast({ text: "Line taken out.", icon: "check", action: { label: "Undo", onClick: back } });
  };

  // --- the pieces ---
  const moveTo = (id: string, was: number, sec: number) => {
    const to = Math.max(PIN_START, Math.min(sec, dur - PIN_END));
    if (to === was) return;
    editing.typeOn(id, { t: to });
    editing.typedOn(id, () => ({ t: was }));
  };
  const momentFields = shown ? (
    <MomentFields
      moment={shown}
      live={live}
      off={false}
      edit={editing.typeOn}
      typed={editing.typedOn}
      onTime={(sec) => moveTo(shown.id, shown.t ?? 0, sec)}
      file={handoutPick(shown, lib.assets, editing)}
    />
  ) : null;
  const offerFields = offer ? (
    <OfferFields
      offer={offer}
      live={live}
      off={false}
      edit={editing.typeOn}
      typed={editing.typedOn}
      onTime={(sec) => moveTo(offer.id, offer.t ?? 0, sec)}
      file={offerFilePick(offer, lib.assets, editing)}
      videoNote={videoNotePick(offer, lib.assets, notePoster, mmss, editing)}
    />
  ) : (
    <p className="k-sub">This room has no offer yet. Add one on a computer.</p>
  );

  const offerSection = (
    <>
      <h2 className="k-gh">The offer</h2>
      {offer ? <OfferRows offer={offer} live={live} onOpen={(focus) => (setOfferShown(true), setOpen({ k: "offer", focus }))} /> : offerFields}
    </>
  );

  const pageFrame = (
    <SeasonPageFrame studio={studio} tablet={!phone} selected={phone ? null : part === WHOLE_PAGE ? null : part} peek={peek && part !== WHOLE_PAGE} peekPage={peek && part === WHOLE_PAGE} onSlot={onSlot} onPart={onPart} />
  );
  const room = (
    <RoomPreview state={state} host={host} at={second} phone title={roomName} />
  );
  const roomFrame = phone ? (
    <div className="mk-box">{room}</div>
  ) : (
    <div className="bw-phwrap">
      <div className="bw-phone bw-auto">
        <div className="bw-phone-in">{room}</div>
      </div>
    </div>
  );
  const preview = step === "page" ? pageFrame : roomFrame;
  const addressRow = (
    <GroupedList className="mk-after">
      <ListRow icon="link" title="Address" sub={address.replace(/^https?:\/\//, "")} onClick={() => setOpen({ k: "address" })} />
    </GroupedList>
  );
  const bigger = (
    <p className="mk-bigscr">
      <Icon name="monitor" /> Edit on a bigger screen
    </p>
  );

  const addFollow = () => setOpen({ k: "addFollow" });
  const follows = (
    <FollowRows
      mails={mails}
      host={host}
      busy={!!host.busy}
      onAdd={addFollow}
      onOpen={(m) => {
        if (phone || layout === "upright") setOpen({ k: "follow", id: m.id });
        else setFsel(m.id);
      }}
    />
  );

  const chatThread = studio.threads[chatSection] ?? [];
  const chatProps = {
    thread: chatThread,
    busy: studio.working === chatSection,
    onAsk: (text: string, files: Parameters<typeof studio.ask>[2]) => studio.ask(chatSection, text, files),
    onBack: (id: string) => studio.back(chatSection, id),
    placeholder: chatSection === WHOLE_PAGE ? "What should change on the page?" : "What should change here?",
    error: studio.errorIn(chatSection) || undefined,
    phone,
  };
  const chatCard = (
    <SeasonCard className="mk-chat" label={label(part)}>
      <div className="mk-ch">
        <b>{label(part)}</b>
        {part === WHOLE_PAGE ? null : (
          <button type="button" className="k-link" onClick={() => setPart(WHOLE_PAGE)}>
            The whole page
          </button>
        )}
      </div>
      <Thread {...chatProps} onPeek={setPeek} />
      <Composer {...chatProps} />
    </SeasonCard>
  );

  const side =
    step === "page" ? (
      chatCard
    ) : step === "room" ? (
      <>
        <h2 className="k-gh mk-gh0">Moments</h2>
        <MomentRows moments={moments} live={live} shown={offerShown ? null : (shown?.id ?? null)} onShow={(id) => (setShownId(id), setOfferShown(false))} />
        <div className="k-form mk-ff mk-mf">{momentFields}</div>
        {offerSection}
        {bigger}
      </>
    ) : null;

  // --- the action ---
  const last = stepAt === WALK_STEPS.length - 1;
  const onNext = () => w.next(() => setOpen({ k: "publish" }));
  const nextLabel = last ? (published ? "Looks right" : "Publish") : "Looks right";
  const nextButton = (full: boolean) => (
    <Button variant="p" size="lg" full={full} after={last ? undefined : "right"} icon={last && !published ? "send" : undefined} onClick={onNext}>
      {nextLabel}
    </Button>
  );
  const menuButton = (
    <button type="button" className="k-ib" aria-label="More" onClick={() => setOpen({ k: "menu" })}>
      <Icon name="more" />
    </button>
  );
  const copyAddress = () => {
    void navigator.clipboard?.writeText(host.liveUrl || `https://${base}/${state.slug}`).catch(() => undefined);
    toast({ text: "Copied", icon: "copy" });
  };

  // --- the sheets ---
  const sheets = (
    <>
      {open?.k === "text" ? <TextSheet tapped={open.tapped} onSave={(t) => saveSlot(open.tapped, t)} onOut={() => takeOut(open.tapped)} onClose={close} /> : null}
      {open?.k === "address" ? (
        <AddressSheet
          base={base}
          slug={state.slug}
          onClose={close}
          onSave={(slug) => {
            close();
            if (slug !== state.slug) {
              host.saveSlug(slug);
              toast("Address saved");
            }
          }}
        />
      ) : null}
      {open?.k === "moment" && shown ? (
        <FieldsSheet title={KIND[shown.kind].name} onClose={close}>
          {momentFields}
        </FieldsSheet>
      ) : null}
      {open?.k === "offer" ? (
        <OfferSheet focus={open.focus} onClose={close}>
          {offerFields}
        </OfferSheet>
      ) : null}
      {open?.k === "follow" ? (
        (() => {
          const m = mails.find((x) => x.id === open.id);
          return m ? <FollowSheet host={host} m={m} onClose={close} /> : null;
        })()
      ) : null}
      {open?.k === "addFollow" ? <AddFollowSheet state={state} host={host} sessions={sessions} onClose={close} /> : null}
      {open?.k === "menu" ? (
        <ActionsSheet
          open
          onClose={close}
          title="More"
          actions={[
            { icon: "link", label: "Copy the page’s address", onSelect: copyAddress },
            ...(published ? [{ icon: "pause" as const, label: "Unpublish", onSelect: () => host.unpublish() }] : []),
            { icon: "refresh", label: live ? "Start over from your notes" : "Start over from the talk", onSelect: () => setOpen({ k: "over" }) },
          ]}
        />
      ) : null}
      {open?.k === "over" ? (
        <StartOverSheet
          live={live}
          threads={studio.threads}
          onClose={close}
          onStart={() => {
            close();
            host.readAgain();
          }}
        />
      ) : null}
      {open?.k === "publish" ? (
        <PublishSheet
          state={state}
          host={host}
          looked={looked}
          address={address}
          onClose={close}
          onGo={(s) => {
            close();
            go(s);
          }}
          onGoOffer={() => {
            if (step === "room") {
              setOfferShown(true);
              setOpen({ k: "offer", focus: null });
              return;
            }
            offerNext.current = true;
            go("room");
          }}
        />
      ) : null}
      {open?.k === "page" && studio.doc ? <OpenPage address={address} doc={studio.doc} onClose={close} /> : null}
    </>
  );

  const notes = (
    <>
      {host.message ? (
        <p className="note warn" role="status">
          <Icon name="info" />
          <span>{host.message}</span>
        </p>
      ) : null}
      {host.warnings.length ? (
        <p className="note warn" role="status">
          <Icon name="info" />
          <span>{host.warnings[0]}</span>
          <button type="button" className="k-ib" aria-label="Dismiss" onClick={host.dismissWarnings}>
            <Icon name="x" />
          </button>
        </p>
      ) : null}
    </>
  );

  // --- the published screen ---
  if (showPublished) {
    const copy = () => {
      void navigator.clipboard?.writeText(host.liveUrl || `https://${address}`).catch(() => undefined);
      toast({ text: "Copied", icon: "copy" });
    };
    const copyBtn = (
      <Button variant="p" size="lg" full={phone} icon="link" onClick={copy}>
        Copy link
      </Button>
    );
    const core = (
      <div className="mk-pubh">
        <span className="mk-bigck big">
          <Icon name="check" />
        </span>
        <span className="pill good">
          <i />
          Live
        </span>
        <h1 className="k-h1 mk-h1">The page is live</h1>
        <p className="mk-link mono">{address.replace(/^https?:\/\//, "")}</p>
      </div>
    );
    const rows = (
      <GroupedList>
        <ListRow icon="ext" title="Open the page" onClick={() => setOpen({ k: "page" })} />
        <ListRow icon="play" title="See it in Rooms" to={ROOMS} />
      </GroupedList>
    );
    const back = { to: "/", label: "Back", onBack: () => setShowPublished(false) };
    return (
      <Page layout={layout} className={phone ? "mk" : "mk mk-pubw"}>
        {phone ? (
          <>
            <PushedNav back={back} />
            {core}
            {rows}
            <DockedBar>{copyBtn}</DockedBar>
          </>
        ) : (
          <>
            {layout === "upright" ? (
              <div className="k-head">
                <PushedNav back={back} />
              </div>
            ) : null}
            <SeasonCard className="mk-pubc">
              {core}
              <div className="mk-cf mk-cfc">{copyBtn}</div>
              {rows}
            </SeasonCard>
          </>
        )}
        {sheets}
      </Page>
    );
  }

  // --- a phone: one stop to a screen ---
  if (phone) {
    if (chat) {
      return (
        <Page layout="phone" className="mk mk-chatp">
          <PushedNav back={{ to: "/", label: "The page", onBack: backToPage }} title={label(chat)} />
          <Thread {...chatProps} onSee={backToPage} />
          <Composer {...chatProps} />
          {sheets}
        </Page>
      );
    }
    const first = stepAt === 0;
    return (
      <Page layout="phone" className="mk">
        <PushedNav back={{ to: ROOMS, label: first ? "Rooms" : "Back", onBack: () => (first ? w.navigate(ROOMS) : go(WALK_STEPS[stepAt - 1])) }} right={menuButton} />
        <StopDots step={step} looked={looked} onGo={go} />
        <h1 className="k-h1 mk-h1">{STEP_NAMES[step]}</h1>
        {notes}
        {step === "page" ? (
          <>
            <GroupedList className="mk-first">
              <ListRow icon="palette" title="Change the whole page" onClick={() => openChat(WHOLE_PAGE)} end={asksIn(studio.threads[WHOLE_PAGE]) ? <span className="pill wine">{asksIn(studio.threads[WHOLE_PAGE])}</span> : undefined} />
            </GroupedList>
            {preview}
            {addressRow}
          </>
        ) : step === "room" ? (
          <>
            {preview}
            <h2 className="k-gh">Moments</h2>
            <MomentRows moments={moments} live={live} shown={null} onShow={(id) => (setShownId(id), setOfferShown(false), setOpen({ k: "moment", id }))} />
            {offerSection}
            {bigger}
          </>
        ) : (
          follows
        )}
        <DockedBar>{nextButton(true)}</DockedBar>
        {sheets}
      </Page>
    );
  }

  // --- an iPad upright: the preview and the fields in two columns ---
  const seg = (
    <Switch
      label="The steps"
      value={step}
      onChange={(s: WalkStep) => go(s)}
      options={WALK_STEPS.map((s) => ({ value: s, label: STEP_NAMES[s], icon: looked.has(s) ? ("check" as const) : undefined }))}
    />
  );
  const acts = (
    <div className="mk-acts">
      {menuButton}
      {nextButton(false)}
    </div>
  );
  if (layout === "upright") {
    return (
      <Page layout="upright" className="mk">
        <div className="k-head mk-head">
          <button type="button" className="k-back" onClick={() => (stepAt ? go(WALK_STEPS[stepAt - 1]) : w.navigate(ROOMS))}>
            <Icon name="left" />
            <span>{stepAt ? "Back" : "Rooms"}</span>
          </button>
          {acts}
        </div>
        {seg}
        {notes}
        {step === "followups" ? (
          <div className="mk-wide">{follows}</div>
        ) : (
          <div className="k-two mk-two2">
            <div>
              {preview}
              {step === "page" ? addressRow : null}
            </div>
            <div>{side}</div>
          </div>
        )}
        {sheets}
      </Page>
    );
  }

  // --- an iPad on its side: the stops, the preview, the fields or the chat ---
  const followDetail = followNow ? (
    <>
      <h2 className="k-gh mk-gh0">{followName(host, followNow)}</h2>
      <FollowSteps host={host} m={followNow} />
      {followNow.automationId ? (
        <Button className="mk-ed" icon="zap" onClick={() => w.navigate(`/automations/${followNow.automationId}`)}>
          Edit the steps in Follow-ups
        </Button>
      ) : null}
    </>
  ) : null;
  return (
    <Panes three>
      <Pane kind="list" label="The steps">
        <div className="k-sp-lh">
          <button type="button" className="k-back" onClick={() => w.navigate(ROOMS)}>
            <Icon name="left" />
            <span>Rooms</span>
          </button>
          <h1 className="k-title mk-wt">Walk it through</h1>
        </div>
        <Page layout="side" className="mk mk-lp">
          <StopList step={step} looked={looked} followCount={mails.length} onGo={go} />
        </Page>
      </Pane>
      <Pane kind="main" showing={step} label={STEP_NAMES[step]}>
        <Page layout="side" className="mk">
          <div className="mk-mhead">{acts}</div>
          {notes}
          {step === "followups" ? <div className="mk-narrow">{follows}</div> : preview}
          {step === "page" ? addressRow : null}
        </Page>
      </Pane>
      <Pane kind="detail" showing={step} label="Fields">
        <Page layout="side" className="mk">
          {step === "followups" ? followDetail : side}
        </Page>
      </Pane>
      {sheets}
    </Panes>
  );
}

/** The offer's fields in a sheet, the one a row named taking the caret. */
function OfferSheet({ focus, onClose, children }: { focus: string | null; onClose: () => void; children: React.ReactNode }) {
  useEffect(() => {
    if (!focus) return;
    const t = window.setTimeout(() => {
      const el = [...document.querySelectorAll<HTMLElement>(".mk-ff [aria-label]")].find((e) => e.getAttribute("aria-label") === focus);
      el?.focus();
    }, 80);
    return () => window.clearTimeout(t);
  }, [focus]);
  return (
    <FieldsSheet title="The offer" onClose={onClose}>
      {children}
    </FieldsSheet>
  );
}

/** Add a follow-up: the ready-made ones by the library's own names, or a start from a tag. */
function AddFollowSheet({ state, host, sessions, onClose }: { state: BoardState; host: BoardHost; sessions: boolean; onClose: () => void }) {
  const ready = FOLLOW_UP_LIBRARY.filter(([, , , when]) => sessions || when === "any");
  return (
    <Sheet open onClose={onClose} title="Add a follow-up">
      <ul className="k-gl">
        {ready.map(([name, request, kind]) => {
          const taken = libraryTaken(state, host.followUpTriggers, name, kind);
          return (
            <li className="k-lr" key={name}>
              <button
                type="button"
                className="k-lr-b"
                disabled={taken}
                onClick={() => {
                  onClose();
                  host.addFollowUp(request, undefined, name);
                }}
              >
                <span className="k-ico">
                  <Icon name="zap" />
                </span>
                <span className="k-lr-t">
                  <b>{name}</b>
                  <span>{taken ? "Already on this room" : "Written from the talk"}</span>
                </span>
              </button>
            </li>
          );
        })}
        <li className="k-lr">
          <button
            type="button"
            className="k-lr-b"
            onClick={() => {
              onClose();
              void host.startFollowUp();
            }}
          >
            <span className="k-ico">
              <Icon name="tag" />
            </span>
            <span className="k-lr-t">
              <b>Start from a tag</b>
            </span>
          </button>
        </li>
      </ul>
    </Sheet>
  );
}
